import { useContext, useEffect, useState, lazy, Suspense } from "react";
import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import Pusher from "pusher-js";
import Register from "./views/Register";
import Login from "./views/Login";
import AddProfileInfo from "./views/AddProfileInfo";
import AddSchoolInfo from "./views/AddSchoolInfo";
import AddCollegeInfo from "./views/AddCollegeInfo";
import AddProfessionInfo from "./views/AddProfessionInfo";
import RegisterUserProile from "./views/RegisterUserProfile";
import RouteProtected from "./components/RouteProtected";
import AuthenticatedUsers from "./components/AuthenticatedUsers";
import { AuthContext } from "./contexts/AuthContext";
import UserProfileProvider from "./contexts/UserProfileStepsContext";
import LinkedInCallbackComponent from "./components/LinkedInCallback";
import Main from "./views/Main";
import MyNetwork from "./views/MyNetwork";
import Reels from "./views/Reels";
import Loader from "./components/Loader";
import MyPosts from "./views/MyPosts";
import Service from "./views/Service";
import UserDetailView from "./views/UserDetailView";
import ProfileActivity from "./components/ProfileActivity";
import AskQuery from "./views/AskQuery";
import MyQueries from "./components/MyQueries";
import Success from "./views/Success";
import Failure from "./views/Failure";
import FollowerSuggestion from "./views/FollowerSuggestion";
import PrivacyPolicy from "./views/PrivacyPolicy";
import RefundPolicy from "./views/RefundPolicy";
import TermsAndConditions from "./views/TermsAndConditions";
import CustomerSupport from "./views/CustomerSupport";
import Payments from "./views/Payments";
import Chat from "./views/Chat";

const Home = lazy(() => import("./views/Home"));
const Notifications = lazy(() => import("./views/Notifications"));
const MySpace = lazy(() => import("./views/MySpace"));
const MyProfile = lazy(() => import("./views/ProfileView"));
const Bookings = lazy(() => import("./views/Bookings"));
const Schedule = lazy(() => import("./views/Schedule"));
const Queries = lazy(() => import("./views/Queries"));
const Calendar = lazy(() => import("./views/Calendar"));
const Favourite = lazy(() => import("./views/Favourite"));

// import socketIO from "socket.io-client";
import UpcomingSession from "./views/UpcomingSession";
import { LinkedInCallback } from "react-linkedin-login-oauth2";
import AccountSettings from "./views/AccountSettings";
import MyActivity from "./views/Activity";
import Landing from "./views/Pages/Landing";
import Terms from "./views/Pages/Terms";
import Privacy from "./views/Pages/Privacy";
import AboutUs from "./views/Pages/AboutUs";
import Careers from "./views/Pages/Careers";
import ContactUs from "./views/Pages/ContactUs";
import OtherUserPost from "./views/OtherUserPost";
import { chatConfig } from "./shared/const.prop";
import { getActiveNotificationCount } from "./store/actions/notificationAction";
import { useDispatch } from "react-redux";
import Testimonials from "./views/Testimonials";
import Search from "./views/Search";
import Bookmark from "./views/Bookmark";
import { filterReducerConstant } from "./store/reducerConstant";
import filterReducer from "./store/reducers/filterReducer";
import { getFilterList } from "./store/actions/filterActions";
import Subscription from "./views/Subscription";
import SignupPage from "./views/SignupPage";
import SignInForm from "./views/SignIn";
import SignUpForm from "./views/SignUp";
import ProfileSetup from "./views/ProfileSetup";
import UserProfileView from "./views/UserProfileView";
import PublicProfile from "./views/PublicProfile";
import FAQ from "./views/Pages/FAQ";
import Blog from "./views/Pages/Blogs/mainBlog";
import FromLearnerToMentor from "./views/Pages/Blogs/FromLearnerToMentor";
import IrresistibleProfileOnUpskillMeet from "./views/Pages/Blogs/IrresistibleProfileonUpskillMeet";
import ProfileThatGetsBookings from "./views/Pages/Blogs/ProfileThatGetsBookings";
import StudentSuccessStories from "./views/Pages/Blogs/StudentSuccessStories";
import MistakesExpertsMake from "./views/Pages/Blogs/MistakesExpertsMake";
import GetHelpfulFeedback from "./views/Pages/Blogs/GetHelpfulFeedback";
import TreatOnlineSessions from "./views/Pages/Blogs/TreatOnlineSessions";
import BookBetterSessions from "./views/Pages/Blogs/BookBetterSessions";
import WhatToPost from "./views/Pages/Blogs/WhatToPost";
import BookExpertsShare from "./views/Pages/Blogs/BookExpertsShare";
import DayInLife from "./views/Pages/Blogs/DayInLife";

export const App = () => {
  // const socket = socketIO.connect(window.location.origin);
  const { user } = useContext(AuthContext);
  const dispatch = useDispatch();
  const [isLoggedIn, setIsLoggedIn] = useState();
  const location = useLocation();

  const userId = user?.userId || user?.data?.data?.userId;

  useEffect(() => {
    setIsLoggedIn(!!localStorage.getItem("token"));
  }, [user]);

  useEffect(() => {
    if (userId) {
      const pusher = new Pusher(chatConfig.apiKey, {
        cluster: chatConfig.cluster,
        encrypted: true,
      });
      const channel = pusher.subscribe(userId);
      channel.bind("notification", (data) => {
        dispatch(getActiveNotificationCount());
      });
      return () => {
        pusher.unsubscribe(userId);
      };
    }
  }, [location.pathname, userId]);

  useEffect(() => {
    window.scrollTo(0, 0); // Scrolls to the top-left corner
  }, [location.pathname]);

  // useEffect(() => {
  //   if (msg) setChats([...chats, msg]);
  // }, [msg]);

  useEffect(() => {
    const currentUrl = location.pathname;

    const setTitleBasedOnUrl = (url) => {
      let title = "Upskillmeet";

      if (url === "/login") {
        title = "Login";
      } else if (url === "/register") {
        title = "Sign up";
      }
      if (url !== "/searchResults") {
        // dispatch(
        //   filterReducer({
        //     type: filterReducerConstant.RESET_FILTER_LIST,
        //   })
        // );

        dispatch({ type: filterReducerConstant.RESET_FILTER_LIST });
      }

      document.title = title;
    };

    setTitleBasedOnUrl(currentUrl);
  }, [location.pathname]);

  return (
    <div className="main">
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/signup-options" element={<SignupPage />} />
        <Route path="/terms" element={<Terms />} />
        {/* <Route path="/privacy" element={<Privacy />} /> */}
        <Route path="/aboutus" element={<AboutUs />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/contactus" element={<ContactUs />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/blog" element={<Blog />} />
        <Route
          path="/blog/learner-to-mentor"
          element={<FromLearnerToMentor />}
        />
        <Route
          path="/blog/irresistible-profile"
          element={<IrresistibleProfileOnUpskillMeet />}
        />
        <Route
          path="/blog/profile-that-gets-bookings"
          element={<ProfileThatGetsBookings />}
        />
        <Route
          path="/blog/student-success-stories"
          element={<StudentSuccessStories />}
        />
        <Route
          path="/blog/mistakes-experts-make"
          element={<MistakesExpertsMake />}
        />
        <Route
          path="/blog/get-helpful-feedback"
          element={<GetHelpfulFeedback />}
        />
        <Route
          path="/blog/treat-online-sessions"
          element={<TreatOnlineSessions />}
        />
        <Route
          path="/blog/book-better-sessions"
          element={<BookBetterSessions />}
        />
        <Route path="/blog/what-to-post" element={<WhatToPost />} />
        <Route path="/blog/book-experts-share" element={<BookExpertsShare />} />
        <Route path="/blog/day-in-life" element={<DayInLife />} />
        <Route
          path="/"
          element={
            <RouteProtected isLoggedIn={isLoggedIn}>
              <Main />
            </RouteProtected>
          }
        >
          <Route
            // index
            path="home"
            element={
              <Suspense fallback={<Loader />}>
                <Home />
              </Suspense>
            }
          />
          <Route path="my-network" element={<MyNetwork />} />
          <Route path="suggested-followers" element={<FollowerSuggestion />} />
          <Route path="/:id" element={<UserProfileView />} />
          {/* <Route path="test/:id" element={<UserProfileView />} /> */}
          <Route
            path="notifications"
            element={
              <Suspense fallback={<Loader />}>
                <Notifications />
              </Suspense>
            }
          />
          {/* <Route
            path="myspace"
            element={
              <Suspense fallback={<Loader />}>
                <MySpace />
              </Suspense>
            }
          /> */}
          <Route
            path="myspace/calendar"
            element={
              <Suspense fallback={<Loader />}>
                <Calendar />
              </Suspense>
            }
          />
          <Route
            path="myspace/queries"
            element={
              <Suspense fallback={<Loader />}>
                <Queries />
              </Suspense>
            }
          />
          <Route
            path="myspace/my-queries"
            element={
              <Suspense fallback={<Loader />}>
                <MyQueries />
              </Suspense>
            }
          />
          <Route path="user/:id/ask-query" element={<AskQuery />} />
          <Route path="videos" element={<Reels />} />
          <Route
            path="people"
            element={<ProfileActivity isLayoutView={true} />}
          />
          <Route
            path="home"
            element={
              <Suspense fallback={<Loader />}>
                <Home />
              </Suspense>
            }
          />
          <Route
            path="myspace/testimonials"
            element={
              <Suspense fallback={<Loader />}>
                <Testimonials />
              </Suspense>
            }
          />
          <Route
            path="myspace/post"
            element={
              <Suspense fallback={<Loader />}>
                <MyPosts />
              </Suspense>
            }
          />
          {/* <Route
            path="myspace/promotions"
            element={
              <Suspense fallback={<Loader />}>
                <MyPosts isPromotion={true} />
              </Suspense>
            }
          /> */}
          <Route
            path="my-profile"
            element={
              <Suspense fallback={<Loader />}>
                <MyProfile />
              </Suspense>
            }
          />
          <Route
            path="myspace/network"
            element={
              <Suspense fallback={<Loader />}>
                <MyActivity />
              </Suspense>
            }
          />
          <Route
            path="myspace/services"
            element={
              <Suspense fallback={<Loader />}>
                <Service />
              </Suspense>
            }
          />
          <Route path="myspace/payments" element={<Payments />} />
          <Route path="myspace/subscription" element={<Subscription />} />
          <Route
            path="user/:id/services"
            element={<Service isOtherUser={true} />}
          />
          <Route path="user/:id/posts" element={<OtherUserPost />} />
          <Route
            path="user/:id/promotions"
            element={<OtherUserPost isPromotion={true} />}
          />
          <Route
            path="user/:id/services/bookings"
            element={
              <Suspense fallback={<Loader />}>
                <Bookings />
              </Suspense>
            }
          />
          <Route
            path="user/:id/testimonials"
            element={
              <Suspense fallback={<Loader />}>
                <Testimonials />
              </Suspense>
            }
          />
          <Route
            path="myspace/schedule"
            element={
              <Suspense fallback={<Loader />}>
                <Schedule />
              </Suspense>
            }
          />
          <Route
            path="myspace/schedule/:id"
            element={
              <Suspense fallback={<Loader />}>
                <UpcomingSession />
              </Suspense>
            }
          />
          <Route
            path="chat"
            element={
              <Suspense fallback={<Loader />}>
                <Chat />
              </Suspense>
            }
          />
          <Route
            path="searchResults"
            element={
              <Suspense fallback={<Loader />}>
                <Search />
              </Suspense>
            }
          />
          <Route
            path="favourite-users"
            element={
              <Suspense fallback={<Loader />}>
                <Favourite />
              </Suspense>
            }
          />
          <Route
            path="bookmark"
            element={
              <Suspense fallback={<Loader />}>
                <Bookmark />
              </Suspense>
            }
          />
          <Route path="account-settings" element={<AccountSettings />} />
        </Route>
        <Route path="privacy-policy" element={<PrivacyPolicy />} />
        <Route path="refund-policy" element={<RefundPolicy />} />
        <Route path="tnc" element={<TermsAndConditions />} />
        <Route path="customer-support" element={<CustomerSupport />} />
        <Route path="payment-success" element={<Success />} />
        <Route path="payment-failure" element={<Failure />} />
        <Route path="public-profile" element={<PublicProfile />} />

        <Route
          path="register"
          element={
            <AuthenticatedUsers isLoggedIn={isLoggedIn}>
              <SignUpForm />
            </AuthenticatedUsers>
          }
        />
        <Route
          path="login"
          element={
            <AuthenticatedUsers isLoggedIn={isLoggedIn}>
              <SignInForm />
            </AuthenticatedUsers>
          }
        />
        <Route
          path="profile-setup"
          element={
            <UserProfileProvider>
              <ProfileSetup />
            </UserProfileProvider>
          }
        ></Route>
        <Route
          path="add-profile"
          element={
            <UserProfileProvider>
              <RegisterUserProile />
            </UserProfileProvider>
          }
        >
          <Route
            index
            element={
              <AuthenticatedUsers isLoggedIn={isLoggedIn}>
                <AddProfileInfo />
              </AuthenticatedUsers>
            }
          />

          <Route path="step1" element={<Navigate to="../" />} />
          <Route path="step2" element={<AddSchoolInfo />} />
          <Route path="step3" element={<AddCollegeInfo />} />
          <Route path="step4" element={<AddProfessionInfo />} />
        </Route>
        {/* <Route path="linkedin" element={<LinkedInCallbackComponent />} /> */}

        <Route path="/linkedin/callback" component={LinkedInCallback} />
      </Routes>
    </div>
  );
};
